import { useState, useMemo } from 'react';
import { api } from '../lib/api.js';
import { useFormFields } from '../hooks/useFormFields.js';
import { useDebounce } from '../hooks/useDebounce.js';
import { validateMultipleEmails, validateOptionalMultipleEmails, formatMultipleEmails } from '@gng-ehs/shared';

export interface Company {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name: string;
  factory_address: string;
  license_number?: string;
  license_expiry_date?: string;
  registration_number?: string;
  nic_code?: string;
  contact_email?: string;
  communication_email?: string;
}

interface InlineCompanyFormProps {
  initialCompany?: Company;
  embedded?: boolean;
  onCreated: (company: Company) => void;
  onCancel: () => void;
}

export function InlineCompanyForm({ onCreated, onCancel, initialCompany, embedded = false }: InlineCompanyFormProps) {
  const Container = embedded ? 'div' : 'form';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [checkingShortCode, setCheckingShortCode] = useState(false);
  const [shortCodeStatus, setShortCodeStatus] = useState<{ available?: boolean; message?: string; suggested?: string } | null>(null);

  const fields = useFormFields({
    short_code: '',
    company_name: '',
    occupier_name: '',
    factory_address: '',
    license_number: '',
    license_expiry_date: '',
    registration_number: '',
    nic_code: '',
    contact_email: '',
    communication_email: '',
    ...Object.fromEntries(Object.entries(initialCompany || {}).filter(([key, value]) => ['short_code', 'company_name', 'occupier_name', 'factory_address', 'license_number', 'license_expiry_date', 'registration_number', 'nic_code', 'contact_email', 'communication_email'].includes(key) && typeof value === 'string')),
  });

  const debouncedShortCode = fields.values.short_code;
  useDebounce(debouncedShortCode, () => {
    const sc = fields.values.short_code.trim();
    if (!sc) {
      setShortCodeStatus(null);
      return;
    }
    setCheckingShortCode(true);
    api.companies.checkShortCode(sc, undefined, initialCompany?.id)
      .then((res) => {
        if (res.data) {
          setShortCodeStatus(res.data);
        }
      })
      .catch(() => setShortCodeStatus(null))
      .finally(() => setCheckingShortCode(false));
  });

  const handleSuggestShortCode = async () => {
    const { company_name } = fields.values;
    if (!company_name.trim()) {
      setError('Please enter a Company Name first to suggest a Short Code.');
      return;
    }
    setCheckingShortCode(true);
    try {
      const res = await api.companies.suggestShortCode(company_name.trim(), initialCompany?.id);
      if (res.data?.short_code) {
        fields.setField('short_code', res.data.short_code);
      }
    } catch {
      /* ignore */
    } finally {
      setCheckingShortCode(false);
    }
  };

  const isFormValid = useMemo(() => {
    const {
      short_code, company_name, occupier_name, factory_address,
      license_number, registration_number, contact_email, communication_email,
    } = fields.values;
    return (
      !!short_code.trim() &&
      !!company_name.trim() &&
      occupierNameValid(occupier_name) &&
      factoryAddressValid(factory_address) &&
      licenseNumberValid(license_number) &&
      registrationNumberValid(registration_number) &&
      validateMultipleEmails(contact_email) &&
      validateOptionalMultipleEmails(communication_email) &&
      (shortCodeStatus?.available !== false)
    );
  }, [fields.values, shortCodeStatus]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    const {
      short_code, company_name, occupier_name, factory_address,
      license_number, registration_number, contact_email, communication_email,
    } = fields.values;

    if (!short_code.trim()) { setError('Short Code is required.'); return; }
    if (!company_name.trim()) { setError('Company Name is required.'); return; }
    if (!occupierNameValid(occupier_name)) { setError('Occupier Name is required.'); return; }
    if (!factoryAddressValid(factory_address)) { setError('Factory Address is required.'); return; }
    if (!licenseNumberValid(license_number)) { setError('License Number is required.'); return; }
    if (!registrationNumberValid(registration_number)) { setError('Registration Number is required.'); return; }
    if (!validateMultipleEmails(contact_email)) { setError('Valid Contact Email(s) required (separated by commas).'); return; }
    if (!validateOptionalMultipleEmails(communication_email)) { setError('Please enter valid Communication Email address(es).'); return; }

    if (shortCodeStatus?.available === false) {
      setError(shortCodeStatus.message || 'Short Code is already taken.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...fields.values,
        contact_email: formatMultipleEmails(contact_email) || '',
        communication_email: formatMultipleEmails(communication_email) || '',
        short_code: short_code.trim().toUpperCase(),
      };
      const res = initialCompany ? await api.companies.update(initialCompany.id, payload) : await api.companies.create(payload);
      const created = (res as { data?: Company }).data;
      if (created) {
        onCreated(created);
      } else {
        throw new Error('Failed to create company');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-300 bg-emerald-50/90 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider">{initialCompany ? 'Update Company Details' : 'Create New Company'}</p>
        <button type="button" onClick={onCancel} className="text-emerald-600 hover:text-emerald-800 text-xs font-bold">✕</button>
      </div>

      {error && (
        <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>✕</button>
        </div>
      )}

      <Container onSubmit={embedded ? undefined : handleSubmit} className="space-y-3 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Short Code <span className="text-red-500">*</span>
            </label>
            <div className="space-y-1">
              <div className="flex gap-1.5">
                <input
                  className="input text-xs font-mono uppercase bg-white"
                  {...fields.bind('short_code')}
                  placeholder="e.g. SPPL"
                  required
                />
                <button
                  type="button"
                  onClick={handleSuggestShortCode}
                  className="px-2.5 py-1 text-xs font-semibold bg-white text-emerald-800 border border-emerald-300 rounded-lg shrink-0 hover:bg-emerald-100 cursor-pointer"
                >
                  Suggest
                </button>
              </div>
              {checkingShortCode && (
                <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1">
                  <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                  Checking availability…
                </p>
              )}
              {!checkingShortCode && shortCodeStatus && fields.values.short_code.trim() && (
                shortCodeStatus.available ? (
                  <p className="text-[11px] text-emerald-700 font-semibold">
                    ✓ Short code '{fields.values.short_code.toUpperCase()}' is available
                  </p>
                ) : (
                  <div className="text-[11px] text-red-600 font-semibold space-y-0.5">
                    <p>✕ {shortCodeStatus.message}</p>
                    {shortCodeStatus.suggested && (
                      <p>
                        Suggested code:{' '}
                        <button
                          type="button"
                          onClick={() => fields.setField('short_code', shortCodeStatus.suggested!)}
                          className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                        >
                          Use '{shortCodeStatus.suggested}'
                        </button>
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              {...fields.bind('company_name')}
              placeholder="Full Company Name"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Occupier Name <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              {...fields.bind('occupier_name')}
              placeholder="Occupier / Director Name"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Factory Address <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              {...fields.bind('factory_address')}
              placeholder="Full Factory Address"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              License No <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              {...fields.bind('license_number')}
              placeholder="Factory License No"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              License Expiry Date
            </label>
            <input
              className="input text-xs bg-white"
              type="date"
              {...fields.bind('license_expiry_date')}
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Reg No <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              {...fields.bind('registration_number')}
              placeholder="Factory Registration No"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Contact Email(s) <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-xs bg-white"
              type="text"
              {...fields.bind('contact_email')}
              placeholder="e.g. contact@company.com, admin@company.com"
              autoComplete="off"
              required
            />
          </div>

          <div>
            <label className="label text-[11px] font-semibold text-gray-700 mb-1">
              Communication Email
            </label>
            <input
              className="input text-xs bg-white"
              type="text"
              {...fields.bind('communication_email')}
              placeholder="e.g. comms@company.com"
              autoComplete="off"
            />
          </div>
        </div>

        {error && (
          <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')}>✕</button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-emerald-200">
          <button type="button" onClick={onCancel} className="btn btn-secondary text-xs">
            Cancel
          </button>
          <button type={embedded ? "button" : "submit"} onClick={embedded ? handleSubmit : undefined} disabled={saving || !isFormValid} className="btn btn-primary text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving Company…' : initialCompany ? 'Save & Update Company Details' : 'Save & Select Company'}
          </button>
        </div>
      </Container>
    </div>
  );
}

function occupierNameValid(v: string) { return !!v.trim(); }
function factoryAddressValid(v: string) { return !!v.trim(); }
function licenseNumberValid(v: string) { return !!v.trim(); }
function registrationNumberValid(v: string) { return !!v.trim(); }
